import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element (hamburger toggle).
 * @property {HTMLDialogElement} menuDrawer - The dialog bottom-sheet panel.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    this.addEventListener('keyup', this.#onKeyUp, { signal });
    this.refs.menuDrawer.addEventListener('cancel', this.#onDialogCancel, { signal });
    this.refs.menuDrawer.addEventListener('click', this.#onDialogClick, { signal });
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Close the main drawer when Escape is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    // Only close main drawer if no submenu is open
    if (!this.refs.menuDrawer.querySelector('.menu-open')) {
      this.close();
    }
  };

  /**
   * Prevent native Escape from closing the dialog without animation.
   * @param {Event} event
   */
  #onDialogCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  /**
   * Close when clicking on the ::backdrop (click target is the dialog itself).
   * @param {MouseEvent} event
   */
  #onDialogClick = (event) => {
    if (event.target === this.refs.menuDrawer) {
      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.menuDrawer.open;
  }

  /**
   * Get the closest details element to the event target (for submenu navigation).
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    // Only match child details inside the drawer (submenus), not the outer toggle details
    const closest = event.target.closest('details');
    if (closest && closest !== this.refs.details) return closest;
    return this.refs.details;
  }

  /**
   * Toggle the main menu drawer.
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the main drawer or a submenu.
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);

    if (details === this.refs.details) {
      // ── Main menu open ──
      const summary = this.refs.details.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', 'true');

      this.preventInitialAccordionAnimations(this.refs.menuDrawer);

      const scrollY = window.scrollY;
      this.#previousScrollY = scrollY;

      requestAnimationFrame(() => {
        // Lock body scroll (same pattern as dialog.js / cart drawer)
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;

        this.refs.details.classList.add('menu-open');
        this.refs.menuDrawer.showModal();

        onAnimationEnd(this.refs.menuDrawer, () => trapFocus(this.refs.menuDrawer), { subtree: false });
      });
    } else {
      // ── Submenu open ──
      const summary = details.querySelector('summary');
      if (!summary) return;
      summary.setAttribute('aria-expanded', 'true');

      this.preventInitialAccordionAnimations(details);
      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        if (target) {
          this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
        }

        const submenu = details.querySelector('.menu-drawer__submenu');
        onAnimationEnd(submenu || details, () => trapFocus(details), { subtree: false });
      });
    }
  }

  /**
   * Go back or close the main drawer.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main drawer.
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the main drawer or a submenu.
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', 'false');

    if (details === this.refs.details) {
      // ── Main menu close ──
      const drawer = this.refs.menuDrawer;

      // Trigger slide-down animation (same pattern as cart's dialog-closing)
      drawer.style.animation = 'none';
      void drawer.offsetWidth;
      drawer.classList.add('dialog-closing');
      drawer.style.animation = '';

      onAnimationEnd(drawer, () => {
        drawer.close();
        drawer.classList.remove('dialog-closing');
        this.refs.details.classList.remove('menu-open');
        this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

        // Restore body scroll (same pattern as dialog.js / cart drawer)
        document.documentElement.style.scrollBehavior = 'auto';
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        window.scrollTo(0, this.#previousScrollY);
        document.documentElement.style.scrollBehavior = '';

        removeTrapFocus();

        // Reset any open submenus inside the drawer
        const openSubmenus = drawer.querySelectorAll('details[open]:not(accordion-custom > details)');
        openSubmenus.forEach(resetDetails);

        // Reset filter accordions
        drawer.querySelectorAll('.menu-filter__accordion').forEach((acc) => {
          acc.dataset.open = 'false';
          acc.querySelector('.menu-filter__summary')?.setAttribute('aria-expanded', 'false');
          const sublist = acc.querySelector('.menu-filter__sublist');
          if (sublist) sublist.setAttribute('aria-hidden', 'true');
        });
      }, { subtree: false });
    } else {
      // ── Submenu close ──
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

      const submenu = details.querySelector('.menu-drawer__submenu');
      onAnimationEnd(submenu || details, () => {
        resetDetails(details);
        trapFocus(this.refs.menuDrawer);
      }, { subtree: false });
    }
  }

  /**
   * Attach animationend listeners to remove will-change after stagger animation
   * so submenus can be positioned correctly.
   */
  #setupAnimatedElementListeners() {
    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disable accordion open animations to prevent jitter on menu open.
   * @param {Element} container
   */
  preventInitialAccordionAnimations(container) {
    const content = container.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original closed state.
 * @param {HTMLDetailsElement} element
 */
function resetDetails(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
